/**
 * RequestedChangesPage — admin triage for user-submitted improvement
 * requests.  Surfaces every row with action buttons matching the
 * backend status machine:
 *
 *   pending → [Submit to Codex] → approved_to_build
 *   approved_to_build → [Mark under construction] → under_construction
 *   under_construction → [Mark built (preview)] → built_pending_deploy
 *   built_pending_deploy → [Open preview] · [Mark deployed] → deployed
 *   any → [Decline]
 *
 * Pre-deploy gate at the top: shows a red banner when any
 * built_pending_deploy item is missing manuals_updated, blocking the
 * "Re-deploy app" button until docs catch up.
 */
import { useEffect, useState, useMemo } from "react";
import { Lightbulb, ArrowsClockwise, CheckCircle, Wrench, Hammer, Rocket, Eye, X, Warning, Trash, ClockClockwise, BookBookmark, Plus, PaperPlaneTilt, ShieldStar, Paperclip, DownloadSimple, Copy, ArrowSquareOut, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import AdminPageHeader from "@/components/AdminPageHeader";
import RequestedChangesTour from "@/components/RequestedChangesTour";

const STATUS_ORDER = ["pending", "approved_to_build", "under_construction", "built_pending_deploy", "deployed", "declined"];
const STATUS_LABEL = {
  pending: "Pending review",
  approved_to_build: "Sent to Codex",
  under_construction: "Under construction",
  built_pending_deploy: "Built · awaiting redeploy",
  deployed: "Deployed",
  declined: "Declined",
};
const STATUS_TONE = {
  pending: "bg-amber-100 text-amber-900 border-amber-300",
  approved_to_build: "bg-blue-100 text-blue-900 border-blue-300",
  under_construction: "bg-violet-100 text-violet-900 border-violet-300",
  built_pending_deploy: "bg-orange-100 text-orange-900 border-orange-300",
  deployed: "bg-emerald-100 text-emerald-900 border-emerald-300",
  declined: "bg-slate-200 text-slate-600 border-slate-300",
};

export default function RequestedChangesPage({ user, onLogout, onLogoutAll }) {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [readiness, setReadiness] = useState(null);
  const [showDeployModal, setShowDeployModal] = useState(false);
  // iter150c — inline "New request" modal so admins don't have to hunt
  // for the SuggestChangeButton pill in the header.
  const [showNewModal, setShowNewModal] = useState(false);
  const [newSubject, setNewSubject] = useState("");
  const [newBody, setNewBody] = useState("");
  const [newFiles, setNewFiles] = useState([]);  // iter150e — staged files
  const [submittingNew, setSubmittingNew] = useState(false);
  // iter150h — Approve-with-prompt modal so super-admins see a ready-to-
  // paste Codex prompt instead of a bare browser confirm.
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveNote, setApproveNote] = useState("");
  const [approvePrompt, setApprovePrompt] = useState("");
  const isSuper = !!user?.is_super_admin;

  const ATTACH_ALLOWED_EXT = [
    ".pdf", ".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".heic", ".heif",
    ".bmp", ".tiff", ".mp4", ".mov", ".webm", ".m4v",
    ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".odt", ".ods", ".odp",
    ".csv", ".txt", ".md", ".rtf", ".log", ".zip",
  ];
  const ATTACH_BLOCKED_EXT = [
    ".exe", ".bat", ".cmd", ".com", ".scr", ".msi", ".ps1", ".vbs", ".vbe",
    ".js", ".mjs", ".cjs", ".jse", ".wsf", ".wsh", ".jar", ".class",
    ".sh", ".bash", ".zsh", ".fish", ".csh", ".ksh",
    ".py", ".pyc", ".pyo", ".pyd", ".rb", ".pl", ".php", ".phtml", ".cgi",
    ".dll", ".so", ".dylib", ".app", ".apk", ".ipa", ".deb", ".rpm",
    ".reg", ".inf", ".lnk", ".url", ".scf", ".chm", ".hta",
  ];

  const stageFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const accepted = [];
    for (const f of incoming) {
      const lower = (f.name || "").toLowerCase();
      const dot = lower.lastIndexOf(".");
      const ext = dot >= 0 ? lower.slice(dot) : "";
      if (!ext) {
        toast.error(`"${f.name}" has no extension — please rename and retry.`);
        continue;
      }
      if (ATTACH_BLOCKED_EXT.includes(ext)) {
        toast.error(`"${f.name}" is an executable/script and cannot be attached.`);
        continue;
      }
      if (!ATTACH_ALLOWED_EXT.includes(ext)) {
        toast.error(`"${f.name}" — file type ${ext} is not supported.`);
        continue;
      }
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`"${f.name}" is over 25 MB.`);
        continue;
      }
      accepted.push(f);
    }
    setNewFiles((prev) => [...prev, ...accepted].slice(0, 10));
  };

  const submitNewRequest = async (e) => {
    e?.preventDefault?.();
    const subj = newSubject.trim();
    const bdy = newBody.trim();
    if (subj.length < 3) { toast.error("Add a short subject (3+ chars)."); return; }
    if (bdy.length < 5) { toast.error("Describe the change (5+ chars)."); return; }
    setSubmittingNew(true);
    try {
      const r = await api.post("/suggestions", { subject: subj, body: bdy, lang });
      const sid = r.data?.id;
      // Upload any staged files (best-effort — submission still succeeds
      // even if attachment upload fails).
      if (sid && newFiles.length > 0) {
        const fd = new FormData();
        newFiles.forEach((f) => fd.append("files", f, f.name));
        try {
          await api.post(`/suggestions/${encodeURIComponent(sid)}/attachments`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (uErr) {
          toast.error(uErr?.response?.data?.detail || "Files could not be attached.");
        }
      }
      toast.success("Request submitted — pending super-admin approval.");
      setNewSubject(""); setNewBody(""); setNewFiles([]); setShowNewModal(false);
      setFilter("pending");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request.");
    } finally { setSubmittingNew(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filter && filter !== "all") params.status = filter;
      const [r, ready] = await Promise.all([
        api.get("/admin/suggestions", { params }),
        api.get("/admin/suggestions/deploy-readiness"),
      ]);
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || {});
      setReadiness(ready.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load requests");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const total = useMemo(() => Object.values(counts).reduce((a, b) => a + (b || 0), 0), [counts]);

  const setStatus = async (sid, status, extra = {}) => {
    if (busyId) return;
    setBusyId(sid);
    try {
      await api.put(`/admin/suggestions/${encodeURIComponent(sid)}/status`,
                    { status, ...extra });
      toast.success(`→ ${STATUS_LABEL[status] || status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusyId(""); }
  };

  // iter150h — Build the recommended Codex prompt for an approved
  // request. Reads subject/body/attachments and crafts a single
  // copy-paste block the super-admin can drop into the Codex chat.
  const buildCodexPrompt = (it) => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const lines = [];
    lines.push(`Please implement the following user-submitted change in the portal:`);
    lines.push("");
    lines.push(`**Request id:** ${it.id}`);
    lines.push(`**Submitted by:** ${it.submitted_by || "?"}`);
    lines.push(`**Subject:** ${it.subject || ""}`);
    lines.push("");
    lines.push(`**Description:**`);
    lines.push(it.body || "(no body)");
    if ((it.attachments || []).length > 0) {
      lines.push("");
      lines.push(`**Attached files (download to inspect):**`);
      for (const a of it.attachments) {
        const url = `${base}/api/suggestions/${it.id}/attachments/${a.id}`;
        const sizeKB = a.size ? `, ${(a.size / 1024).toFixed(0)} KB` : "";
        lines.push(`- ${a.filename}${sizeKB} — ${url}`);
      }
    }
    lines.push("");
    lines.push(`**Acceptance criteria:**`);
    lines.push(`- Implement the smallest change that satisfies the request.`);
    lines.push(`- Add data-testid attributes on every new interactive element.`);
    lines.push(`- Add a backend pytest under /app/backend/tests/ if you touch a backend route.`);
    lines.push(`- Update /app/memory/PRD.md with a short changelog entry.`);
    lines.push(`- After building, mark this request as built_pending_deploy in /admin/requested-changes with the preview URL.`);
    return lines.join("\n");
  };

  const openApproveModal = (it) => {
    setApproveTarget(it);
    setApproveNote("");
    setApprovePrompt(buildCodexPrompt(it));
  };

  const confirmApprove = async () => {
    if (!approveTarget) return;
    const it = approveTarget;
    setBusyId(it.id);
    try {
      await api.put(`/admin/suggestions/${encodeURIComponent(it.id)}/status`, {
        status: "approved_to_build",
        feasibility_note: approveNote || "",
        codex_prompt: approvePrompt || "",
        note: "Approved — sent to Codex",
      });
      toast.success("Approved — paste the prompt into Codex chat to start the build.");
      setApproveTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setBusyId(""); }
  };

  const copyPromptToClipboard = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(approvePrompt);
        toast.success("Prompt copied. Paste it into the Codex chat.");
        return;
      }
      throw new Error("clipboard api missing");
    } catch {
      // Fallback for non-secure contexts (mobile Safari over HTTP, etc.)
      const ta = document.createElement("textarea");
      ta.value = approvePrompt;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("Prompt copied."); }
      catch { toast.error("Could not copy — please select the text and copy manually."); }
      document.body.removeChild(ta);
    }
  };

  // Legacy `sendToCodex` shim — kept for any older callers; routes to
  // the new modal so super-admins always get the prompt review step.
  const sendToCodex = async (it) => {
    openApproveModal(it);
  };

  const markBuilt = async (it) => {
    const url = window.prompt(
      "Where can admins preview this in the dev env?\n(Path or full URL — e.g. /warranty/list?preview=pdf)",
      it.preview_url || ""
    );
    if (url === null) return;
    setStatus(it.id, "built_pending_deploy", { preview_url: url, note: "Built — preview ready" });
  };

  const decline = async (it) => {
    const reason = window.prompt("Reason for declining (will be saved):", "");
    if (!reason || !reason.trim()) return;
    setStatus(it.id, "declined", { decline_reason: reason.trim() });
  };

  const toggleManuals = async (it) => {
    setStatus(it.id, it.status, {
      manuals_updated: !it.manuals_updated,
      note: it.manuals_updated ? "Manuals reverted" : "Manuals/help/tutorials/onboarding updated",
    });
  };

  const remove = async (it) => {
    if (!isSuper) return;
    if (!window.confirm(`Hard-delete "${it.subject}"? This cannot be undone.`)) return;
    setBusyId(it.id);
    try {
      await api.delete(`/admin/suggestions/${encodeURIComponent(it.id)}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusyId(""); }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-['Lato'] text-slate-900" data-testid="requested-changes-page">
      <AdminPageHeader
        sticky maxWidth="max-w-5xl" testId="requested-changes-back"
        icon={<Lightbulb size={20} weight="fill" className="text-amber-600" />}
        title="Requested changes"
        subtitle="User-submitted improvement requests · approve, build, preview, deploy."
        user={user} onLogout={onLogout} onLogoutAll={onLogoutAll}
      />

      <main className="max-w-5xl mx-auto p-6 pb-32 space-y-5">
        <RequestedChangesTour />

        {/* iter150c — Workflow banner clarifying admin vs super-admin roles */}
        <section className="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-start gap-2"
                 data-testid="requested-changes-workflow-banner">
          <ShieldStar size={16} weight="fill" className="text-blue-700 mt-0.5 flex-shrink-0" />
          <p className="text-[12px] text-blue-900 leading-relaxed">
            <strong>How this works:</strong> Any admin can <strong>+ New request</strong> to submit a change.
            Requests land as <em>Pending review</em>. Only super-admins can <strong>approve</strong> (send to build)
            or <strong>decline</strong>. Once approved, the change moves through build → preview → deploy.
          </p>
        </section>

        {/* Deploy gate banner */}
        {readiness && (
          <section className={`border rounded-md p-4 ${readiness.ready ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}
                   data-testid="requested-changes-deploy-readiness">
            <div className="flex flex-wrap items-center gap-3">
              {readiness.ready
                ? <CheckCircle size={18} weight="fill" className="text-emerald-700" />
                : <Warning size={18} weight="fill" className="text-rose-700" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold">
                  {readiness.ready
                    ? `Ready to redeploy — ${readiness.ready_to_ship} change${readiness.ready_to_ship === 1 ? "" : "s"} waiting to go live`
                    : `Cannot redeploy yet — ${readiness.blockers.length} change${readiness.blockers.length === 1 ? "" : "s"} need manual updates`}
                </p>
                {!readiness.ready && (
                  <ul className="mt-1 text-xs text-rose-800 list-disc ml-5">
                    {readiness.blockers.map((b) => (
                      <li key={b.id}><span className="font-bold">{b.subject}</span> — {b.reason}</li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                onClick={() => setShowDeployModal(true)}
                disabled={!readiness.ready || readiness.ready_to_ship === 0}
                data-testid="redeploy-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-40 disabled:cursor-not-allowed">
                <Rocket size={12} weight="fill" />
                Re-deploy app
              </button>
            </div>
          </section>
        )}

        {/* Filter chips */}
        <section data-testid="requested-changes-filters"
                 className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-1">Filter:</span>
          <FilterChip label="All" count={total} active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all" />
          {STATUS_ORDER.map((s) => (
            <FilterChip key={s} label={STATUS_LABEL[s]} count={counts[s]} active={filter === s}
                        onClick={() => setFilter(s)} testId={`filter-${s}`} />
          ))}
          <button
            onClick={load}
            data-testid="requested-changes-refresh"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider border border-slate-200 rounded-sm hover:bg-slate-50">
            <ArrowsClockwise size={11} weight="bold" />
            Refresh
          </button>
          <button
            onClick={() => setShowNewModal(true)}
            data-testid="requested-changes-new-request"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm bg-amber-500 text-white hover:bg-amber-600">
            <Plus size={11} weight="bold" />
            New request
          </button>
        </section>

        {/* List */}
        {loading && (
          <div className="flex items-center justify-center gap-2 py-12 text-slate-500" data-testid="requested-changes-loading">
            <ArrowsClockwise size={14} weight="bold" className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        )}
        {!loading && items.length === 0 && (
          <p className="text-center text-sm text-slate-500 italic py-12" data-testid="requested-changes-empty">
            No requests in this status.
          </p>
        )}
        <ul className="space-y-3" data-testid="requested-changes-list">
          {items.map((it) => {
            const tone = STATUS_TONE[it.status] || "bg-slate-100 text-slate-700 border-slate-300";
            const busy = busyId === it.id;
            return (
              <li key={it.id} className="bg-white border border-slate-200 rounded-md p-4 shadow-sm"
                  data-testid={`requested-change-${it.id}`}>
                <div className="flex flex-wrap items-baseline gap-2 mb-1">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${tone}`}>
                    {STATUS_LABEL[it.status] || it.status}
                  </span>
                  <span className="text-[10px] font-mono text-slate-400">{it.id}</span>
                  <span className="text-[10px] text-slate-500">
                    {it.submitted_by} · {new Date(it.submitted_at).toLocaleDateString()}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-auto">{it.lang || "en"}</span>
                </div>
                <h3 className="text-sm font-bold text-slate-900">{it.subject}</h3>
                <p className="text-xs text-slate-700 leading-relaxed mt-1 whitespace-pre-wrap break-words">{it.body}</p>

                {/* iter150i — AI triage assessment (Claude Sonnet 4.5) */}
                {it.ai_assessment && (
                  <div
                    data-testid={`ai-assessment-${it.id}`}
                    className={`mt-2 flex flex-wrap items-start gap-2 px-3 py-2 rounded-sm text-[11px] border ${
                      it.ai_assessment.risk === "low"
                        ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                        : it.ai_assessment.risk === "medium"
                        ? "bg-amber-50 border-amber-200 text-amber-900"
                        : it.ai_assessment.risk === "high"
                        ? "bg-rose-50 border-rose-200 text-rose-900"
                        : "bg-slate-50 border-slate-200 text-slate-700"
                    }`}>
                    <span className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm shrink-0 ${
                      it.ai_assessment.risk === "low"
                        ? "bg-emerald-600 text-white"
                        : it.ai_assessment.risk === "medium"
                        ? "bg-amber-500 text-white"
                        : it.ai_assessment.risk === "high"
                        ? "bg-rose-600 text-white"
                        : "bg-slate-500 text-white"
                    }`}>
                      <Sparkle size={9} weight="fill" className="mr-1" />
                      {it.ai_assessment.risk === "review" ? "Needs review" : `${it.ai_assessment.risk} risk`}
                    </span>
                    <span className="flex-1 min-w-[200px] leading-relaxed">
                      {it.ai_assessment.summary}
                    </span>
                    {(it.ai_assessment.suggested_areas || []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {it.ai_assessment.suggested_areas.map((a) => (
                          <span key={a}
                                className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm bg-white/70 border border-current/30">
                            {a}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {it.feasibility_note && (
                  <div className="mt-2 bg-blue-50 border-l-2 border-blue-400 px-3 py-1.5 text-[11px] text-blue-900">
                    <span className="font-bold">Feasibility:</span> {it.feasibility_note}
                  </div>
                )}
                {it.decline_reason && (
                  <div className="mt-2 bg-slate-100 border-l-2 border-slate-400 px-3 py-1.5 text-[11px] text-slate-700">
                    <span className="font-bold">Declined:</span> {it.decline_reason}
                  </div>
                )}

                {/* Status-specific banners */}
                {it.status === "approved_to_build" && (
                  <p className="mt-2 text-[11px] italic text-blue-900 bg-blue-50 px-3 py-1.5 rounded-sm">
                    Marked for build. Open Codex chat and reference request <code className="font-mono">{it.id}</code> to start construction.
                  </p>
                )}
                {it.status === "built_pending_deploy" && (
                  <p className="mt-2 text-[11px] italic text-orange-900 bg-orange-50 px-3 py-1.5 rounded-sm">
                    This change is built in preview but NOT deployed to production yet. Test below, then click <strong>Re-deploy app</strong> at the top.
                  </p>
                )}

                {/* iter150e — Attachments */}
                {(it.attachments || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-1.5"
                       data-testid={`attachments-${it.id}`}>
                    <Paperclip size={11} weight="bold" className="text-slate-500" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-1">
                      Attachments ({it.attachments.length})
                    </span>
                    {it.attachments.map((a) => (
                      <a key={a.id}
                         href={`${process.env.REACT_APP_BACKEND_URL}/api/suggestions/${encodeURIComponent(it.id)}/attachments/${encodeURIComponent(a.id)}`}
                         target="_blank" rel="noopener noreferrer"
                         data-testid={`attachment-link-${a.id}`}
                         title={`${a.filename} — ${(a.size / 1024).toFixed(0)} KB`}
                         className="inline-flex items-center gap-1 max-w-[220px] px-2 py-0.5 text-[10px] border border-slate-300 rounded-sm bg-slate-50 hover:bg-blue-50 hover:border-blue-300">
                        <DownloadSimple size={10} weight="bold" />
                        <span className="truncate">{a.filename}</span>
                      </a>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
                  {it.status === "pending" && (
                    isSuper ? (
                      <ActionBtn busy={busy} testId={`send-codex-${it.id}`} onClick={() => sendToCodex(it)}
                                 icon={<PaperPlaneIcon />} tone="blue">Approve · send to Codex</ActionBtn>
                    ) : (
                      <span data-testid={`pending-awaiting-superadmin-${it.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-slate-100 text-slate-600 border border-slate-300">
                        <ShieldStar size={11} weight="fill" /> Awaiting super-admin approval
                      </span>
                    )
                  )}
                  {it.status === "approved_to_build" && (
                    <ActionBtn busy={busy} testId={`mark-construction-${it.id}`} onClick={() => setStatus(it.id, "under_construction", { note: "Build started" })}
                               icon={<Wrench size={11} weight="fill" />} tone="violet">Mark under construction</ActionBtn>
                  )}
                  {it.status === "under_construction" && (
                    <ActionBtn busy={busy} testId={`mark-built-${it.id}`} onClick={() => markBuilt(it)}
                               icon={<Hammer size={11} weight="fill" />} tone="orange">Mark built (preview)</ActionBtn>
                  )}
                  {it.status === "built_pending_deploy" && (
                    <>
                      {it.preview_url && (
                        <a href={it.preview_url} target="_blank" rel="noopener noreferrer"
                           data-testid={`preview-${it.id}`}
                           className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-orange-300 text-orange-700 hover:bg-orange-50">
                          <Eye size={11} weight="fill" /> Open preview
                        </a>
                      )}
                      <button
                        onClick={() => toggleManuals(it)}
                        disabled={busy}
                        data-testid={`toggle-manuals-${it.id}`}
                        title="Have manuals/help/tutorials/onboarding been updated for this change?"
                        className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${
                          it.manuals_updated
                            ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                            : "border-rose-300 text-rose-700 bg-rose-50"}`}>
                        <BookBookmark size={11} weight="fill" />
                        {it.manuals_updated ? "Manuals: updated" : "Manuals: pending"}
                      </button>
                      <ActionBtn busy={busy} testId={`mark-deployed-${it.id}`} onClick={() => setStatus(it.id, "deployed", { note: "Deployed to production" })}
                                 icon={<Rocket size={11} weight="fill" />} tone="emerald">Mark deployed</ActionBtn>
                    </>
                  )}
                  {it.status !== "declined" && it.status !== "deployed" && isSuper && (
                    <ActionBtn busy={busy} testId={`decline-${it.id}`} onClick={() => decline(it)}
                               icon={<X size={11} weight="bold" />} tone="slate">Decline</ActionBtn>
                  )}
                  {it.status !== "pending" && (
                    <ActionBtn busy={busy} testId={`reset-${it.id}`} onClick={() => setStatus(it.id, "pending", { note: "Reset" })}
                               icon={<ClockClockwise size={11} weight="fill" />} tone="amber">Reset to pending</ActionBtn>
                  )}
                  {isSuper && (
                    <button onClick={() => remove(it)} disabled={busy}
                            data-testid={`delete-${it.id}`}
                            title="Hard-delete (super-admin only)"
                            className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-rose-300 text-rose-700 hover:bg-rose-50">
                      <Trash size={11} weight="fill" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </main>

      {/* iter150c — New request modal (any admin can submit) */}
      {showNewModal && (
        <div className="fixed inset-0 z-[400] flex items-start justify-center bg-black/40 p-4 pt-16"
             data-testid="new-request-modal" onClick={() => !submittingNew && setShowNewModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitNewRequest}
                className="bg-white border border-slate-200 rounded-md shadow-xl w-full max-w-md p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Lightbulb size={16} weight="fill" className="text-amber-600" />
              <h2 className="text-sm font-bold tracking-tight">Request a new change</h2>
              <button type="button" onClick={() => setShowNewModal(false)} disabled={submittingNew}
                      data-testid="new-request-close"
                      className="ml-auto text-slate-400 hover:text-slate-700">
                <X size={14} weight="bold" />
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Describe the change you want. It will land as <em>Pending review</em> for a super-admin to
              approve or decline.
            </p>
            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                What's the change?
              </span>
              <input
                type="text"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                placeholder="e.g. Add phone-log export to customer page"
                maxLength={200}
                data-testid="new-request-subject"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none"
                autoFocus
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                Why does it matter?
              </span>
              <textarea
                value={newBody}
                onChange={(e) => setNewBody(e.target.value)}
                placeholder="Describe the problem you hit, who is affected, and how this change would help."
                maxLength={3000}
                rows={5}
                data-testid="new-request-body"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none resize-y"
              />
            </label>
            <div>
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                Attach files (optional)
              </span>
              <label
                htmlFor="new-request-file-input"
                data-testid="new-request-attach-trigger"
                className="inline-flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider border border-slate-300 rounded-sm cursor-pointer hover:bg-slate-50">
                <Paperclip size={11} weight="bold" />
                Choose files
              </label>
              <input
                id="new-request-file-input"
                type="file"
                multiple
                onChange={(e) => { stageFiles(e.target.files); e.target.value = ""; }}
                data-testid="new-request-files"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.svg,.heic,.heif,.bmp,.tiff,.mp4,.mov,.webm,.m4v,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.csv,.txt,.md,.rtf,.log,.zip"
              />
              <p className="mt-1 text-[10px] text-slate-500">
                Up to 10 files, 25 MB each. PDFs, images, video, Office docs, CSV, TXT, ZIP.
                Executable / script files are not allowed.
              </p>
              {newFiles.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="new-request-staged-files">
                  {newFiles.map((f, i) => (
                    <li key={i}
                        className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1 text-[11px]">
                      <Paperclip size={10} weight="bold" className="text-slate-500" />
                      <span className="flex-1 truncate" title={f.name}>{f.name}</span>
                      <span className="text-slate-400 tabular-nums">{(f.size / 1024).toFixed(0)} KB</span>
                      <button type="button"
                              onClick={() => setNewFiles((prev) => prev.filter((_, idx) => idx !== i))}
                              data-testid={`new-request-remove-file-${i}`}
                              className="text-rose-600 hover:text-rose-800">
                        <X size={11} weight="bold" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button type="button" onClick={() => setShowNewModal(false)} disabled={submittingNew}
                      className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-200 rounded-sm hover:bg-slate-50">
                Cancel
              </button>
              <button type="submit" disabled={submittingNew}
                      data-testid="new-request-submit"
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50">
                <PaperPlaneTilt size={11} weight="fill" />
                {submittingNew ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* iter150h — Approve · with recommended Codex prompt */}
      {approveTarget && (
        <div className="fixed inset-0 z-[400] flex items-start justify-center bg-black/40 p-3 pt-8 sm:pt-16 overflow-y-auto"
             data-testid="approve-prompt-modal"
             onClick={() => busyId === "" && setApproveTarget(null)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white border border-slate-200 rounded-md shadow-xl w-full max-w-2xl mb-12">
            <header className="flex items-center gap-2 px-4 sm:px-5 py-3 border-b border-emerald-200 bg-emerald-50 sticky top-0 z-10">
              <CheckCircle size={16} weight="fill" className="text-emerald-700" />
              <h2 className="text-sm font-bold tracking-tight">Approve &amp; copy Codex prompt</h2>
              <button type="button" onClick={() => setApproveTarget(null)} disabled={!!busyId}
                      data-testid="approve-prompt-close"
                      className="ml-auto text-slate-400 hover:text-slate-700">
                <X size={14} weight="bold" />
              </button>
            </header>
            <div className="p-4 sm:p-5 space-y-3">
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <div className="flex items-center gap-2 mb-1">
                  <code className="text-[10px] font-mono text-slate-500">{approveTarget.id}</code>
                  <span className="text-[10px] text-slate-500">· {approveTarget.submitted_by}</span>
                </div>
                <p className="text-sm font-bold text-slate-900">{approveTarget.subject}</p>
                <p className="text-[11px] text-slate-600 mt-1 line-clamp-3">{approveTarget.body}</p>
                {(approveTarget.attachments || []).length > 0 && (
                  <p className="text-[10px] text-slate-500 mt-2">
                    {approveTarget.attachments.length} attachment(s) included in the prompt.
                  </p>
                )}
              </div>

              <label className="block">
                <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Optional feasibility note (your private comment for the build)
                </span>
                <input
                  type="text"
                  value={approveNote}
                  onChange={(e) => setApproveNote(e.target.value)}
                  placeholder="e.g. ETA 1 day, no DB migration needed"
                  data-testid="approve-prompt-note"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-emerald-500 focus:outline-none"
                />
              </label>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">
                    Recommended Codex prompt
                  </span>
                  <button type="button" onClick={copyPromptToClipboard}
                          data-testid="approve-prompt-copy"
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-blue-300 text-blue-800 hover:bg-blue-50">
                    <Copy size={10} weight="bold" /> Copy
                  </button>
                </div>
                <textarea
                  value={approvePrompt}
                  onChange={(e) => setApprovePrompt(e.target.value)}
                  rows={14}
                  data-testid="approve-prompt-text"
                  className="w-full px-3 py-2 text-[11px] font-mono leading-relaxed border border-slate-300 rounded-sm focus:border-emerald-500 focus:outline-none resize-y bg-slate-50"
                />
                <p className="mt-1 text-[10px] text-slate-500">
                  Edit freely. The text above will be saved on the request for audit, and copied to your clipboard when you press <strong>Copy</strong>. Paste it into the Codex chat to start the build.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-sm px-3 py-2 text-[11px] text-blue-900">
                <strong>Next:</strong> open the Codex platform chat (same place you redeploy from), paste the prompt, and let the agent build. When it's ready, mark this request <em>Built · awaiting redeploy</em> below.
              </div>
            </div>
            <footer className="flex items-center justify-end gap-2 px-4 sm:px-5 py-3 border-t border-slate-200 bg-slate-50 sticky bottom-0">
              <button type="button" onClick={() => setApproveTarget(null)} disabled={!!busyId}
                      className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-200 rounded-sm hover:bg-white">
                Cancel
              </button>
              <a href="https://chat.openai.com" target="_blank" rel="noopener noreferrer"
                 data-testid="approve-prompt-open-codex"
                 className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm border border-blue-300 text-blue-800 hover:bg-blue-50">
                <ArrowSquareOut size={11} weight="bold" /> Open Codex
              </a>
              <button type="button" onClick={confirmApprove} disabled={!!busyId}
                      data-testid="approve-prompt-confirm"
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                <CheckCircle size={11} weight="fill" />
                {busyId ? "Approving…" : "Approve & save"}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Re-deploy instructions modal */}
      {showDeployModal && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center bg-black/40 p-4"
             data-testid="redeploy-modal" onClick={() => setShowDeployModal(false)}>
          <div className="bg-white border border-slate-200 rounded-md shadow-xl w-full max-w-md p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <Rocket size={18} weight="fill" className="text-blue-700" />
              <h2 className="text-base font-bold">Re-deploy to production</h2>
              <button onClick={() => setShowDeployModal(false)} className="ml-auto text-slate-400 hover:text-slate-700">
                <X size={14} weight="bold" />
              </button>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              The Codex platform handles deployment. To push every <strong>built · awaiting redeploy</strong> change to <span className="font-mono">compressedairportal.com</span>:
            </p>
            <ol className="text-xs text-slate-700 list-decimal ml-5 mt-2 space-y-1">
              <li>Open the Codex chat panel.</li>
              <li>Click the <strong>Deploy</strong> action on the chat composer.</li>
              <li>Confirm the deploy.</li>
              <li>Once deployment finishes, return here and click <strong>Mark deployed</strong> on each shipped row.</li>
            </ol>
            <p className="text-[11px] italic text-slate-500 mt-3">
              The Improvement Log (`/admin/improvement-log`) is the audit trail of what landed in each deploy.
            </p>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setShowDeployModal(false)}
                      data-testid="redeploy-modal-close"
                      className="px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-slate-900 text-white hover:bg-slate-800">
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PaperPlaneIcon() {
  // Minimal placeholder so the import surface stays small.
  return <span style={{ fontSize: 11 }}>↗</span>;
}

function FilterChip({ label, count, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${
        active ? "bg-slate-900 text-white border-slate-900"
               : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
      }`}>
      {label} {count != null ? `(${count})` : ""}
    </button>
  );
}

function ActionBtn({ children, onClick, busy, icon, tone, testId }) {
  const map = {
    blue: "border-blue-300 text-blue-700 hover:bg-blue-50",
    violet: "border-violet-300 text-violet-700 hover:bg-violet-50",
    orange: "border-orange-300 text-orange-700 hover:bg-orange-50",
    emerald: "border-emerald-300 text-emerald-700 hover:bg-emerald-50",
    slate: "border-slate-300 text-slate-700 hover:bg-slate-50",
    amber: "border-amber-300 text-amber-700 hover:bg-amber-50",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${map[tone] || ""} disabled:opacity-50`}>
      {icon}
      {children}
    </button>
  );
}
